import React from 'react';
import { LucideProps } from 'lucide-react'; // If you want to type it
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import dynamic from 'next/dynamic';
import { forwardRef, ReactNode } from 'react';

interface IconProps extends LucideProps {
  name: keyof typeof dynamicIconImports;
}

const Icon = ({ name, ...props }: IconProps) => {
  const LucideIcon = dynamic(dynamicIconImports[name]);

  return <LucideIcon {...props} />;
};

export const BlogIconManuscript = ({ size = 24, color = "currentColor", strokeWidth = 1, className, ...props }: LucideProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* The Document Outline */}
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    
    {/* Regular Text Lines */}
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    
    {/* The AI 'Spark' or Highlight Line */}
    <path d="M10 9H8" />
    <path d="M16 11l2 2-2 2" strokeOpacity="0.8" /> {/* Optional highlight style */}
  </svg>
);

export const BlogIconFeed = ({ size = 24, color = "currentColor", strokeWidth = 1, className, ...props }: LucideProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* Stacking articles */}
    <rect x="3" y="3" width="18" height="4" rx="1" />
    <rect x="3" y="10" width="18" height="4" rx="1" />
    <rect x="3" y="17" width="18" height="4" rx="1" />
    
    {/* The 'Flow' indicator on the side */}
    <path d="M21 5v14a2 2 0 0 1-2 2H5" strokeOpacity="0.5" />
  </svg>
);

export const BlogIconQuill = ({ size = 24, color = "currentColor", strokeWidth = 1, className, ...props }: LucideProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* The Quill Path */}
    <path d="M21 3s-4.5 4.5-9 9c-1.5-1.5-3-3-4.5-4.5s-3 3-4.5 4.5c1.5 1.5 3 3 4.5 4.5s4.5-4.5 4.5-4.5l9-9z" />
    <path d="M12 12c-1.5 1.5-3.5 3.5-5.5 5.5l-2.5 2.5a1 1 0 0 1-1.4-1.4l2.5-2.5c2-2 4-4 5.5-5.5" />
    
    {/* Grid Dots (The Structure) */}
    <circle cx="16" cy="16" r="1" fill={color} stroke="none" />
    <circle cx="16" cy="20" r="1" fill={color} stroke="none" />
    <circle cx="20" cy="16" r="1" fill={color} stroke="none" />
    <circle cx="20" cy="20" r="1" fill={color} stroke="none" />
  </svg>
);

// Define the interface that lucide uses
export interface CustomIconProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
  strokeWidth?: number;
  color?: string;
  children?: ReactNode;
}

// Wrap your component
export const BlogIcon = forwardRef<SVGSVGElement, CustomIconProps>(
  ({ color = 'currentColor', size = 24, strokeWidth = 2, children, className, ...props }: LucideProps, ref) => {
    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        {/* Concept 1 paths here... */}
      </svg>
    );
  }
);
BlogIcon.displayName = 'BlogIcon';

const COMPONENT_MAP = {
  BlogIconManuscript: BlogIconManuscript,
  BlogIconFeed: BlogIconFeed,
  BlogIconQuill: BlogIconQuill,
  BlogIcon: BlogIcon
} as const;

export enum CustomIcons {
    BlogIconManuscript= "BlogIconManuscript",
    BlogIconFeed= "BlogIconFeed",
    BlogIconQuill= "BlogIconQuill",
    BlogIcon= "BlogIcon"
}

export type ComponentName = keyof typeof COMPONENT_MAP;

export const DynamicIconRenderer = ({ name, className, strokeWidth, props }
    : { name: ComponentName, className: string, strokeWidth: number, props?: any }) => {
  const ComponentToRender = COMPONENT_MAP[name];

  if (!ComponentToRender) return <div>Component {name} not found</div>;

  return <ComponentToRender className={className} strokeWidth={strokeWidth} {...props} />;
};

export default Icon;